import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Spinner,
  Flex,
  IconButton,
  Tooltip,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import PhoneStatusBadge from "./sms/PhoneStatusBadge";
import { useState, useEffect } from "react";
import {
  RepeatIcon,
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  DeleteIcon,
  ViewIcon,
} from "@chakra-ui/icons";
import { supabaseSchema } from "../components/supabaseClient";

const ScheduledSMSModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [messageTypeFilter, setMessageTypeFilter] = useState("auto_reschedule"); // Default to auto_reschedule
  const [cancellingId, setCancellingId] = useState(null);
  const toast = useToast();
  const [counts, setCounts] = useState({
    scheduled: 0,
    sent: 0,
    failed: 0,
    cancelled: 0,
  });

  // For viewing full message
  const {
    isOpen: isMessageOpen,
    onOpen: onMessageOpen,
    onClose: onMessageClose,
  } = useDisclosure();
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchScheduledMessages = async () => {
    setLoading(true);
    try {
      const { getBackendURL } = await import("./env");
      const baseUrl = getBackendURL();

      let url = `${baseUrl}/twilio/scheduled-sms/?schema=${encodeURIComponent(supabaseSchema)}`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      if (messageTypeFilter) {
        url += `&message_type=${messageTypeFilter}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || `Server error: ${response.status}`);
        return;
      }

      if (data.status === "success") {
        setMessages(data.messages || []);
        setCounts(
          data.counts || {
            scheduled: 0,
            sent: 0,
            failed: 0,
            cancelled: 0,
          },
        );
        setError(null);
      } else if (data.status === "error") {
        setError(data.error || "Unknown error");
      }
    } catch (error) {
      console.error("Error fetching scheduled SMS:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchScheduledMessages();
    }
  }, [isOpen, statusFilter, messageTypeFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const handleCancelScheduled = async (messageId) => {
    setCancellingId(messageId);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? "http://localhost:8001"
          : "https://agenotes-backend-production.up.railway.app";

      const response = await fetch(`${baseUrl}/twilio/cancel-scheduled-sms/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message_id: messageId,
          schema: supabaseSchema,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        toast({
          title: "Scheduled SMS Cancelled",
          description: "The scheduled message has been cancelled successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Refresh the list
        fetchScheduledMessages();
      } else {
        toast({
          title: "Failed to Cancel",
          description: data.error || "Could not cancel the scheduled message.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error cancelling scheduled SMS:", error);
      toast({
        title: "Error",
        description:
          "An error occurred while cancelling the scheduled message.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: "blue", icon: <TimeIcon /> },
      sent: { color: "green", icon: <CheckCircleIcon /> },
      failed: { color: "red", icon: <WarningIcon /> },
      cancelled: { color: "gray", icon: <TimeIcon /> },
      processing: { color: "orange", icon: <RepeatIcon /> },
    };
    const config = statusConfig[status] || { color: "gray", icon: null };
    return (
      <Badge
        colorScheme={config.color}
        display="flex"
        alignItems="center"
        gap={1}
      >
        {config.icon}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const handleViewMessage = (msg) => {
    setSelectedMessage(msg);
    onMessageOpen();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxHeight="90vh">
          <ModalHeader>
            <HStack justify="space-between" pr={8}>
              <Text>Auto-Rescheduled SMS Messages</Text>
              <Text fontSize="sm" color="gray.500">
                Failed due to exceeded length - auto-rescheduled for retry
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={4} align="stretch">
              {/* Filter */}
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  Shows only SMS messages that failed due to exceeded length and
                  were auto-rescheduled
                </Text>
                <HStack spacing={2}>
                  <Select
                    size="sm"
                    w="180px"
                    value={messageTypeFilter}
                    onChange={(e) => setMessageTypeFilter(e.target.value)}
                  >
                    <option value="auto_reschedule">
                      Auto-Reschedule Only
                    </option>
                  </Select>
                  <Select
                    size="sm"
                    w="140px"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </Select>
                </HStack>
              </HStack>

              {/* Table */}
              <Box
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                overflow="hidden"
              >
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Status</Th>
                      <Th>Patient Name</Th>
                      <Th>Phone</Th>
                      <Th>Message Preview</Th>
                      <Th>Form Type</Th>
                      <Th>Scheduled For</Th>
                      <Th>Attempts</Th>
                      <Th>Processed At</Th>
                      <Th>Error</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {error ? (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={8}>
                          <Text color="red.500" fontWeight="bold">
                            Error
                          </Text>
                          <Text color="red.400" mt={2}>
                            {error}
                          </Text>
                        </Td>
                      </Tr>
                    ) : loading ? (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={8}>
                          <Spinner size="lg" color="blue.500" />
                          <Text mt={2}>Loading scheduled messages...</Text>
                        </Td>
                      </Tr>
                    ) : messages.length === 0 ? (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={8}>
                          <Text color="gray.500">
                            {statusFilter === "all"
                              ? "No scheduled messages found"
                              : `No ${statusFilter} messages found`}
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      messages.map((msg) => (
                        <Tr key={msg.id} _hover={{ bg: "gray.50" }}>
                          <Td>{getStatusBadge(msg.status)}</Td>
                          <Td>{msg.patient_name || "Unknown"}</Td>
                          <Td>{msg.phone || "N/A"}<PhoneStatusBadge phone={msg.phone} compact ml={1} /></Td>
                          <Td maxW="200px">
                            {msg.message_preview ? (
                              <HStack spacing={2}>
                                <Text
                                  isTruncated
                                  color="blue.600"
                                  fontWeight="medium"
                                  flex={1}
                                >
                                  {msg.message_preview}
                                </Text>
                                <Tooltip
                                  label="View full message"
                                  placement="top"
                                >
                                  <IconButton
                                    icon={<ViewIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="blue"
                                    aria-label="View message"
                                    onClick={() => handleViewMessage(msg)}
                                  />
                                </Tooltip>
                              </HStack>
                            ) : (
                              <Text color="gray.400" fontStyle="italic">
                                No message
                              </Text>
                            )}
                          </Td>
                          <Td>{msg.form_type || "N/A"}</Td>
                          <Td>{formatDate(msg.scheduled_for)}</Td>
                          <Td textAlign="center">{msg.attempts || 0}</Td>
                          <Td>{formatDate(msg.processed_at)}</Td>
                          <Td>
                            {msg.error_message ? (
                              <Tooltip
                                label={msg.error_message}
                                placement="top"
                              >
                                <Badge colorScheme="red" cursor="help">
                                  {msg.error_code || "Error"}
                                </Badge>
                              </Tooltip>
                            ) : (
                              <Text color="gray.400">-</Text>
                            )}
                          </Td>
                          <Td>
                            {msg.status === "scheduled" && (
                              <Tooltip
                                label="Cancel this scheduled message"
                                placement="top"
                              >
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  aria-label="Cancel scheduled message"
                                  onClick={() => handleCancelScheduled(msg.id)}
                                  isLoading={cancellingId === msg.id}
                                />
                              </Tooltip>
                            )}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Full Message Preview Modal */}
      <Modal
        isOpen={isMessageOpen}
        onClose={onMessageClose}
        size="lg"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Message Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedMessage && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" color="gray.600" fontSize="sm">
                    Patient:
                  </Text>
                  <Text>{selectedMessage.patient_name || "Unknown"}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.600" fontSize="sm">
                    Phone:
                  </Text>
                  <Text>{selectedMessage.phone || "N/A"}<PhoneStatusBadge phone={selectedMessage.phone} ml={2} /></Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.600" fontSize="sm">
                    Message:
                  </Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                    maxH="300px"
                    overflowY="auto"
                  >
                    <Text whiteSpace="pre-wrap" wordBreak="break-word">
                      {selectedMessage.message_preview || "No message content"}
                    </Text>
                  </Box>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.600" fontSize="sm">
                    Status:
                  </Text>
                  {getStatusBadge(selectedMessage.status)}
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.600" fontSize="sm">
                    Scheduled For:
                  </Text>
                  <Text>{formatDate(selectedMessage.scheduled_for)}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ScheduledSMSModal;
